import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Container, Card, Spinner, Table, Tab, Tabs, Form, Row, Col, Button, Alert, Badge } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import api from '../api/axiosConfig';
import { BillableAnalysis, BillingState, Invoice, InvoiceStatus, OrganizationDto, PageDto, UserDto } from '../types';
import { isAdmin } from '../auth';
import CreateInvoiceModal from '../components/CreateInvoiceModal';
import EditInvoicePricesModal from '../components/EditInvoicePricesModal';
import { groupByDoctor } from '../utils/groupByDoctor';

const stateLabels: Record<BillingState, string> = {
    STARTED: 'Начат',
    ANALYZED: 'Проанализирован',
    DIAGNOSED: 'Диагноз получен',
    REPORTED: 'Отчёт сформирован',
    FAILED: 'Ошибка',
};

const stateVariants: Record<BillingState, string> = {
    STARTED: 'secondary',
    ANALYZED: 'info',
    DIAGNOSED: 'primary',
    REPORTED: 'success',
    FAILED: 'danger',
};

const statusLabels: Record<InvoiceStatus, string> = {
    DRAFT: 'Черновик',
    ISSUED: 'Выставлен',
    PAID: 'Оплачен',
    CANCELLED: 'Отменён',
};

function defaultPeriod(): { from: string; to: string } {
    return last30Days();
}

function last30Days(): { from: string; to: string } {
    const now = new Date();
    const from = new Date(now);
    from.setDate(now.getDate() - 29);
    const iso = (d: Date) => d.toISOString().slice(0, 10);
    return { from: iso(from), to: iso(now) };
}

const ClinicDetail: React.FC = () => {
    const { orgId } = useParams<{ orgId: string }>();
    const organizationId = orgId ? parseInt(orgId, 10) : null;
    const admin = isAdmin();

    const [org, setOrg] = useState<OrganizationDto | null>(null);
    const [period, setPeriod] = useState<{ from: string; to: string }>(defaultPeriod());
    const [usage, setUsage] = useState<BillableAnalysis[]>([]);
    const [usageLoading, setUsageLoading] = useState(false);
    const [usageError, setUsageError] = useState<string | null>(null);

    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [invoicesLoading, setInvoicesLoading] = useState(false);
    const [invoicesError, setInvoicesError] = useState<string | null>(null);

    const [showCreate, setShowCreate] = useState(false);
    const [editPricesInvoiceId, setEditPricesInvoiceId] = useState<number | null>(null);
    const [activeTab, setActiveTab] = useState<string | null>('usage');
    const [members, setMembers] = useState<UserDto[]>([]);
    const [allUsers, setAllUsers] = useState<UserDto[]>([]);
    const [membersLoading, setMembersLoading] = useState(false);
    const [membersError, setMembersError] = useState<string | null>(null);
    const [userToAdd, setUserToAdd] = useState('');

    const loadOrg = useCallback(async () => {
        if (!organizationId) return;
        try {
            const res = await api.get<OrganizationDto>(`/organizations/${organizationId}`);
            setOrg(res.data);
        } catch {
            setOrg({ id: organizationId, name: `Клиника #${organizationId}` });
        }
    }, [organizationId]);

    const loadUsage = useCallback(async () => {
        if (!organizationId) return;
        setUsageLoading(true);
        setUsageError(null);
        try {
            const res = await api.get<BillableAnalysis[]>('/invoices/usage', {
                params: { organizationId, from: period.from, to: period.to },
            });
            setUsage(res.data);
        } catch (e: any) {
            setUsageError(e.response?.data?.message || 'Не удалось загрузить данные использования');
        } finally {
            setUsageLoading(false);
        }
    }, [organizationId, period.from, period.to]);

    const loadInvoices = useCallback(async () => {
        if (!organizationId) return;
        setInvoicesLoading(true);
        setInvoicesError(null);
        try {
            const res = await api.get<PageDto<Invoice>>('/invoices', {
                params: { organizationId, size: 50, sort: 'createdAt,desc' },
            });
            setInvoices(res.data.content);
        } catch (e: any) {
            setInvoicesError(e.response?.data?.message || 'Не удалось загрузить счета');
        } finally {
            setInvoicesLoading(false);
        }
    }, [organizationId]);

    const loadMembers = useCallback(async () => {
        if (!organizationId || !admin) return;
        setMembersLoading(true);
        setMembersError(null);
        try {
            const [membersResponse, usersResponse] = await Promise.all([
                api.get<PageDto<UserDto>>(`/organizations/${organizationId}/users`, { params: { size: 200, sort: 'lastname,asc' } }),
                api.get<UserDto[]>('/users'),
            ]);
            setMembers(membersResponse.data.content ?? []);
            setAllUsers(Array.isArray(usersResponse.data) ? usersResponse.data : []);
        } catch (e: any) {
            setMembersError(e.response?.data?.message || 'Не удалось загрузить сотрудников клиники');
        } finally { setMembersLoading(false); }
    }, [admin, organizationId]);

    useEffect(() => { loadOrg(); }, [loadOrg]);
    useEffect(() => { loadUsage(); }, [loadUsage]);
    useEffect(() => { loadInvoices(); }, [loadInvoices]);
    useEffect(() => { loadMembers(); }, [loadMembers]);

    const availableUsers = useMemo(() => {
        const memberIds = new Set(members.map((member) => member.id));
        return allUsers.filter((user) => !memberIds.has(user.id));
    }, [allUsers, members]);

    const addMember = async () => {
        if (!organizationId || !userToAdd) return;
        try {
            await api.post(`/organizations/${organizationId}/users/${userToAdd}`);
            setUserToAdd('');
            await loadMembers();
        } catch (e: any) { setMembersError(e.response?.data?.message || 'Не удалось добавить сотрудника'); }
    };

    const removeMember = async (member: UserDto) => {
        if (!organizationId || !window.confirm(`Убрать ${member.username} из клиники?`)) return;
        try { await api.delete(`/organizations/${organizationId}/users/${member.id}`); await loadMembers(); }
        catch (e: any) { setMembersError(e.response?.data?.message || 'Не удалось убрать сотрудника'); }
    };

    const stateCounts = useMemo(() => {
        const counts: Record<BillingState, number> = { STARTED: 0, ANALYZED: 0, DIAGNOSED: 0, REPORTED: 0, FAILED: 0 };
        for (const u of usage) counts[u.billingState] = (counts[u.billingState] || 0) + 1;
        return counts;
    }, [usage]);

    const usageGroups = useMemo(() => groupByDoctor(usage), [usage]);

    const cancelInvoice = async (invoiceId: number) => {
        if (!window.confirm('Отменить этот счёт? Анализы будут возвращены в раздел доступных для выставления.')) return;
        try {
            await api.post(`/invoices/${invoiceId}/cancel`);
            await loadInvoices();
            await loadUsage();
        } catch (e: any) {
            alert(e.response?.data?.message || 'Не удалось отменить счёт');
        }
    };

    const downloadInvoicePdf = async (invoiceId: number, invoiceNumber: string) => {
        try {
            const res = await api.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.download = `${invoiceNumber}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (e: any) {
            alert(e.response?.data?.message || 'Не удалось скачать PDF');
        }
    };

    if (!organizationId) return null;

    return (
        <Container className="mt-4">
            {admin && (
                <Link to="/clinics" className="text-decoration-none muted-small d-inline-flex align-items-center mb-2">
                    <i className="bi bi-arrow-left me-1"></i>Все клиники
                </Link>
            )}
            <div className="page-heading">
                <div>
                    <h2><i className="bi bi-hospital text-primary me-2"></i>{org?.name ?? 'Клиника'}</h2>
                    {org?.shortName && <div className="muted-small">Короткое имя: <code>{org.shortName}</code></div>}
                </div>
            </div>

            <Tabs activeKey={activeTab ?? 'usage'} onSelect={setActiveTab} className="mb-3">
                <Tab eventKey="usage" title="Использование">
                    <Card className="shadow-sm mb-3">
                        <Card.Body>
                            <Row className="align-items-end g-3">
                                <Col md={3}>
                                    <Form.Label className="small text-muted">С</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={period.from}
                                        onChange={(e) => setPeriod({ ...period, from: e.target.value })}
                                    />
                                </Col>
                                <Col md={3}>
                                    <Form.Label className="small text-muted">По</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={period.to}
                                        onChange={(e) => setPeriod({ ...period, to: e.target.value })}
                                    />
                                </Col>
                                <Col md="auto">
                                    <Button variant="outline-secondary" size="sm"
                                            onClick={() => setPeriod(last30Days())}>
                                        Последние 30 дней
                                    </Button>
                                </Col>
                                <Col className="d-flex gap-2 flex-wrap">
                                    {(['REPORTED', 'DIAGNOSED', 'ANALYZED', 'STARTED', 'FAILED'] as BillingState[]).map((s) => (
                                        <Badge key={s} bg={stateVariants[s]} className="px-3 py-2">
                                            {stateLabels[s]}: {stateCounts[s]}
                                        </Badge>
                                    ))}
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {usageError && <Alert variant="danger">{usageError}</Alert>}
                    <Card className="shadow-sm">
                        <Card.Body className="p-0">
                            {usageLoading ? (
                                <div className="d-flex justify-content-center py-5"><Spinner animation="border" /></div>
                            ) : (
                                <Table hover responsive className="mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Номер анализа</th>
                                            <th>Номер отчёта</th>
                                            <th>Состояние</th>
                                            <th>Дата запуска анализа</th>
                                            <th>Дата текущего статуса</th>
                                            <th>Счёт</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {usageGroups.map((g) => (
                                            <React.Fragment key={g.doctorId ?? 'unknown'}>
                                                <tr className="table-secondary">
                                                    <td colSpan={6} className="fw-semibold">
                                                        Врач: {g.doctorName} <span className="text-muted fw-normal">({g.items.length})</span>
                                                    </td>
                                                </tr>
                                                {g.items.map((u) => (
                                                    <tr key={u.analysisId}>
                                                        <td><code style={{ fontSize: 11 }}>{u.analysisId}</code></td>
                                                        <td><code>{u.reportNumber ?? '—'}</code></td>
                                                        <td><Badge bg={stateVariants[u.billingState]}>{stateLabels[u.billingState]}</Badge></td>
                                                        <td>{u.startedAt?.replace('T', ' ').slice(0, 16) ?? '—'}</td>
                                                        <td>{u.terminalStateAt?.replace('T', ' ').slice(0, 16) ?? '—'}</td>
                                                        <td>{u.invoiceNumber
                                                            ? <Badge bg="secondary">{u.invoiceNumber}</Badge>
                                                            : <span className="text-muted">Не выставлен</span>}</td>
                                                    </tr>
                                                ))}
                                            </React.Fragment>
                                        ))}
                                        {usage.length === 0 && (
                                            <tr><td colSpan={6} className="text-center text-muted py-4">
                                                Нет анализов за выбранный период
                                            </td></tr>
                                        )}
                                    </tbody>
                                </Table>
                            )}
                        </Card.Body>
                    </Card>
                </Tab>

                {admin && <Tab eventKey="members" title={`Сотрудники (${members.length})`}>
                    {membersError && <Alert variant="danger">{membersError}</Alert>}
                    <Card className="shadow-sm mb-3"><Card.Body>
                        <Row className="g-2 align-items-end">
                            <Col md={8}>
                                <Form.Label>Добавить существующего пользователя</Form.Label>
                                <Form.Select value={userToAdd} onChange={(e) => setUserToAdd(e.target.value)}>
                                    <option value="">Выберите пользователя…</option>
                                    {availableUsers.map((user) => <option key={user.id} value={user.id}>{[user.lastname, user.firstname].filter(Boolean).join(' ') || user.username} — {user.email}</option>)}
                                </Form.Select>
                            </Col>
                            <Col md="auto"><Button disabled={!userToAdd} onClick={addMember}><i className="bi bi-person-plus me-2" />Добавить</Button></Col>
                        </Row>
                    </Card.Body></Card>
                    <Card className="shadow-sm"><Card.Body className="p-0">
                        {membersLoading ? <div className="text-center py-5"><Spinner /></div> : <Table hover responsive className="mb-0 align-middle">
                            <thead className="table-light"><tr><th>Сотрудник</th><th>Контакты</th><th>Роль</th><th /></tr></thead>
                            <tbody>{members.map((member) => <tr key={member.id}>
                                <td><div className="fw-semibold">{[member.lastname, member.firstname, member.patronomic].filter(Boolean).join(' ') || member.username}</div><code>{member.username}</code></td>
                                <td><div>{member.email}</div><small className="text-muted">{member.phone || '—'}</small></td>
                                <td><Badge bg={member.role?.includes('HEAD') ? 'info' : member.role?.includes('ADMIN') ? 'primary' : 'secondary'}>{member.role}</Badge></td>
                                <td className="text-end"><Button size="sm" variant="outline-danger" onClick={() => removeMember(member)}><i className="bi bi-person-dash me-1" />Убрать</Button></td>
                            </tr>)}{members.length === 0 && <tr><td colSpan={4} className="text-center text-muted py-4">В клинике пока нет сотрудников</td></tr>}</tbody>
                        </Table>}
                    </Card.Body></Card>
                </Tab>}

                <Tab eventKey="invoices" title="Счета">
                    <div className="d-flex justify-content-end mb-3">
                        {admin && (
                            <Button variant="primary" onClick={() => setShowCreate(true)}>
                                Создать счёт
                            </Button>
                        )}
                    </div>
                    {invoicesError && <Alert variant="danger">{invoicesError}</Alert>}
                    <Card className="shadow-sm">
                        <Card.Body className="p-0">
                            {invoicesLoading ? (
                                <div className="d-flex justify-content-center py-5"><Spinner animation="border" /></div>
                            ) : (
                                <Table hover responsive className="mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Номер</th>
                                            <th>Период</th>
                                            <th>Статус</th>
                                            <th>Сумма</th>
                                            <th>Позиций</th>
                                            <th>Создан</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invoices.map((inv) => (
                                            <tr key={inv.id}>
                                                <td><code>{inv.invoiceNumber}</code></td>
                                                <td>{inv.periodStart} → {inv.periodEnd}</td>
                                                <td><Badge bg={inv.status === 'CANCELLED' ? 'secondary' : 'success'}>{statusLabels[inv.status] ?? inv.status}</Badge></td>
                                                <td>{inv.totalAmount} {inv.currency}</td>
                                                <td>{inv.items?.length ?? 0}</td>
                                                <td>{inv.createdAt?.replace('T', ' ').slice(0, 16)}</td>
                                                <td className="text-end">
                                                    {admin && inv.status !== 'CANCELLED' && (
                                                        <Button size="sm" variant="outline-primary" className="me-2"
                                                                onClick={() => setEditPricesInvoiceId(inv.id)}>
                                                            Цены
                                                        </Button>
                                                    )}
                                                    <Button size="sm" variant="outline-secondary" className="me-2"
                                                            onClick={() => downloadInvoicePdf(inv.id, inv.invoiceNumber)}>
                                                        PDF
                                                    </Button>
                                                    {admin && inv.status !== 'CANCELLED' && (
                                                        <Button size="sm" variant="outline-danger" onClick={() => cancelInvoice(inv.id)}>
                                                            Отменить
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                        {invoices.length === 0 && (
                                            <tr><td colSpan={7} className="text-center text-muted py-4">Счета не выставлялись</td></tr>
                                        )}
                                    </tbody>
                                </Table>
                            )}
                        </Card.Body>
                    </Card>
                </Tab>
            </Tabs>

            {admin && showCreate && (
                <CreateInvoiceModal
                    organizationId={organizationId}
                    show={showCreate}
                    onClose={() => setShowCreate(false)}
                    onCreated={async () => {
                        setShowCreate(false);
                        await loadInvoices();
                        await loadUsage();
                        setActiveTab('invoices');
                    }}
                />
            )}
            {admin && editPricesInvoiceId != null && (
                <EditInvoicePricesModal
                    invoiceId={editPricesInvoiceId}
                    show={editPricesInvoiceId != null}
                    onClose={() => setEditPricesInvoiceId(null)}
                    onSaved={async () => {
                        setEditPricesInvoiceId(null);
                        await loadInvoices();
                    }}
                />
            )}
        </Container>
    );
};

export default ClinicDetail;
